import axios from 'axios';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi, locationsApi } from '../../api/services';
import type { EdgeOnboardCustomerUser, EdgeOnboardGoLive, Location } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import { EoIcon, EoPage, EoPageHeader, useEoSnackbar } from './eoShared';

// ============================================================================
// MAC parsing — pure, exported for unit tests.
// ============================================================================

export interface ParsedMacs {
  /** Normalized (colon-form, lowercase) MACs — first occurrence only. */
  valid: string[];
  /** Count of tokens that normalized to an already-seen MAC. */
  duplicates: number;
  /** Non-empty tokens that aren't a MAC, with their source line number. */
  invalid: { line: number; token: string }[];
  /** Total non-empty tokens seen (== valid.length + duplicates + invalid.length). */
  parsed: number;
}

const MAC_COLON = /^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/;
const MAC_DASH = /^[0-9a-f]{2}(-[0-9a-f]{2}){5}$/;
const MAC_BARE = /^[0-9a-f]{12}$/;

/** Trim whitespace and a single layer of surrounding single/double quotes. */
function stripToken(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length >= 2) {
    const first = trimmed[0];
    const last = trimmed[trimmed.length - 1];
    if ((first === '"' || first === "'") && first === last) {
      return trimmed.slice(1, -1).trim();
    }
  }
  return trimmed;
}

/** Accept colon / dash / bare-hex forms; return canonical colon form or null. */
function normalizeMac(token: string): string | null {
  const lower = token.toLowerCase();
  if (MAC_COLON.test(lower)) return lower;
  if (MAC_DASH.test(lower)) return lower.replace(/-/g, ':');
  if (MAC_BARE.test(lower)) return (lower.match(/.{2}/g) as string[]).join(':');
  return null;
}

// Raw tag-scanner emissions look like `MAC:BC5729047458,SERIAL:32046;` (the
// same format hexahq's OrderTaggingAssignment normalizes). Supporting it here
// means staff can scan tags straight into the textarea — no intermediate
// spreadsheet, no copy+paste (see SC-6019).
const SCANNER_BLOCK = /MAC:([0-9a-f]{12})(?:\s*,\s*SERIAL:[^;,\r\n]*;?)?/gi;

/**
 * Parse a pasted blob of MAC addresses. Handles raw scanner output
 * (`MAC:...,SERIAL:...;`) first, then splits the remainder on newlines and
 * commas (CSV cells), tolerating surrounding quotes. Classifies each token
 * per its source line: valid (first occurrence), duplicate (already seen after
 * normalization), or invalid.
 */
export function parseMacs(raw: string): ParsedMacs {
  const valid: string[] = [];
  const invalid: { line: number; token: string }[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  let parsed = 0;

  const classify = (norm: string | null, token: string, lineNumber: number) => {
    parsed += 1;
    if (!norm) {
      invalid.push({ line: lineNumber, token });
    } else if (seen.has(norm)) {
      duplicates += 1;
    } else {
      seen.add(norm);
      valid.push(norm);
    }
  };

  raw.split(/\r?\n/).forEach((rawLine, idx) => {
    const lineNumber = idx + 1;
    // Scanner blocks first — their embedded comma (MAC:…,SERIAL:…) must not
    // reach the CSV splitter, and the serial part is noise, not an invalid MAC.
    const line = stripToken(rawLine).replace(SCANNER_BLOCK, (_, hex: string) => {
      classify(normalizeMac(hex), hex, lineNumber);
      return '';
    });
    line.split(',').forEach(cell => {
      const token = stripToken(cell);
      if (!token) return;
      // Short pure integers are row numbers, not MACs — the legacy Google
      // Sheets this replaces are `slip_number,MAC` rows, and pasting one
      // shouldn't drown the validation panel in fake invalids.
      if (/^\d{1,4}$/.test(token)) return;
      classify(normalizeMac(token), token, lineNumber);
    });
  });

  return { valid, duplicates, invalid, parsed };
}

// ============================================================================
// Page
// ============================================================================

export const TagSlipsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, snackbarElement } = useEoSnackbar();
  const [searchParams] = useSearchParams();

  const [selected, setSelected] = React.useState<EdgeOnboardGoLive | null>(null);
  const [raw, setRaw] = React.useState('');
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const didPreselect = React.useRef(false);

  const goLivesQuery = useQuery({
    queryKey: ['edge-onboard', 'go-lives'],
    queryFn: () => edgeOnboardApi.goLives.getAll(),
  });
  const locationsQuery = useQuery({
    queryKey: ['edge-onboard', 'locations-all'],
    queryFn: () => locationsApi.getAll(),
  });
  // include_deleted so a go-live assigned to a since-deleted user still shows
  // who it belongs to instead of reading "Unassigned".
  const usersQuery = useQuery({
    queryKey: ['edge-onboard', 'customer-users', 'all'],
    queryFn: () => edgeOnboardApi.customerUsers.getAll({ include_deleted: true }),
  });

  const locationsById = React.useMemo(() => {
    const map = new Map<number, Location>();
    (locationsQuery.data ?? []).forEach(l => map.set(l.id, l));
    return map;
  }, [locationsQuery.data]);

  const usersById = React.useMemo(() => {
    const map = new Map<number, EdgeOnboardCustomerUser>();
    (usersQuery.data ?? []).forEach(u => map.set(u.id, u));
    return map;
  }, [usersQuery.data]);

  const labelFor = React.useCallback(
    (g: EdgeOnboardGoLive) => locationsById.get(g.location_id)?.name ?? `Location #${g.location_id}`,
    [locationsById],
  );

  const secondaryFor = React.useCallback(
    (g: EdgeOnboardGoLive) => {
      const loc = locationsById.get(g.location_id);
      const user = usersById.get(g.customer_user_id);
      const hq = loc?.hexahq_account_id ? `HQ #${loc.hexahq_account_id}` : 'No HQ link';
      const name = user
        ? `${user.first_name} ${user.last_name ?? ''}${user.deleted_at ? ' (deleted)' : ''}`.trim()
        : 'Unassigned';
      return `${hq} · ${name}`;
    },
    [locationsById, usersById],
  );

  // Preselect from ?go_live_id=123 once the go-lives have loaded.
  React.useEffect(() => {
    if (didPreselect.current || !goLivesQuery.data) return;
    const param = searchParams.get('go_live_id');
    if (!param) {
      didPreselect.current = true;
      return;
    }
    const match = goLivesQuery.data.find(g => String(g.id) === param);
    // Complete go-lives are un-pickable in the dropdown; a deep link must not
    // sneak one past that guard (the server would 409 the submit anyway).
    if (match && match.state !== 'Complete') setSelected(match);
    didPreselect.current = true;
  }, [goLivesQuery.data, searchParams]);

  // Existing slip count for the selected go-live — drives the "continue at #N"
  // helper so the admin knows where numbering picks up.
  const slipsQuery = useQuery({
    queryKey: ['edge-onboard', 'go-lives', selected?.id, 'tag-slips'],
    queryFn: () => edgeOnboardApi.tagSlips.getAll(selected!.id),
    enabled: Boolean(selected),
  });

  const parsedResult = React.useMemo(() => parseMacs(raw), [raw]);
  const { valid, duplicates, invalid, parsed } = parsedResult;
  const validCount = valid.length;

  // Row numbers for the raw input (Landon): "row 7 of what I scanned" while
  // the cursor is still in the box. Raw INPUT position — distinct from the
  // slip-number preview on the right, which numbers the deduped valid MACs
  // continuing the go-live's existing slip sequence. '' splits to [''], so an
  // empty box shows a single "1". The gutter is overflow-hidden and synced to
  // the textarea's scrollTop (maxRows 16 means the textarea can overflow).
  const lineCount = React.useMemo(() => raw.split('\n').length, [raw]);
  const gutterRef = React.useRef<HTMLDivElement>(null);

  // The go-live id travels through mutation variables, not the live `selected`
  // state — changing the selection mid-request must not redirect the success
  // handling (or crash it) onto the wrong go-live.
  const createMutation = useMutation({
    mutationFn: ({ goLiveId, macs }: { goLiveId: number; macs: string[] }) =>
      edgeOnboardApi.tagSlips.createBulk(goLiveId, macs),
    onSuccess: (created, { goLiveId }) => {
      const ids = created.map(s => s.slip_id);
      const first = Math.min(...ids);
      const last = Math.max(...ids);
      showSuccess(
        created.length === 1
          ? `Created 1 tag slip (#${first})`
          : `Created ${created.length} tag slips (#${first}–#${last})`,
      );
      setRaw('');
      setSubmitError(null);
      void queryClient.invalidateQueries({ queryKey: ['edge-onboard', 'go-lives'] });
      void queryClient.invalidateQueries({
        queryKey: ['edge-onboard', 'go-lives', goLiveId, 'tag-slips'],
      });
    },
    onError: err => {
      // The server names the offenders (409 `detail` = colliding MAC;
      // 422 `mac_addresses` = malformed/duplicate list) — on a 500-MAC paste
      // the generic message alone is undebuggable.
      const data = axios.isAxiosError(err) ? (err.response?.data as Record<string, unknown> | undefined) : undefined;
      const offenders = [
        typeof data?.detail === 'string' ? data.detail : null,
        Array.isArray(data?.mac_addresses) ? (data?.mac_addresses as string[]).join(', ') : null,
      ].filter(Boolean).join(' — ');
      const base = extractApiError(err, 'Failed to create tag slips');
      setSubmitError(offenders ? `${base} (${offenders})` : base);
    },
  });

  const handleSubmit = () => {
    if (!selected || selected.state === 'Complete' || validCount === 0) return;
    setSubmitError(null);
    createMutation.mutate({ goLiveId: selected.id, macs: valid });
  };

  const canSubmit =
    Boolean(selected) && selected?.state !== 'Complete' && validCount > 0 && !createMutation.isPending;

  const loadError = goLivesQuery.error || locationsQuery.error || usersQuery.error;

  const slipCount = slipsQuery.data?.length ?? 0;
  const numberingHelper =
    slipCount === 0
      ? 'No slips yet — numbering starts at #1.'
      : `This go-live has ${slipCount} slips — new ones continue at #${slipCount + 1}.`;

  return (
    <EoPage>
      <EoPageHeader
        title="Add Tag Slips"
        sub="Paste MAC addresses to create sequential tag slips for a go-live."
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiError(loadError, 'Failed to load go-lives')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* ---- Left column: pick a go-live, paste MACs, submit ---- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Autocomplete
            options={goLivesQuery.data ?? []}
            value={selected}
            onChange={(_, value) => {
              setSelected(value);
              setSubmitError(null);
            }}
            loading={goLivesQuery.isLoading}
            disabled={createMutation.isPending}
            getOptionLabel={labelFor}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionDisabled={g => g.state === 'Complete'}
            renderOption={(props, g) => {
              const complete = g.state === 'Complete';
              return (
                <Tooltip
                  key={g.id}
                  title={complete ? 'Complete — reopen to add slips' : ''}
                  placement="right"
                >
                  <span>
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>{labelFor(g)}</Box>
                          {complete && (
                            <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>
                              Complete — reopen to add slips
                            </Box>
                          )}
                        </Box>
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, fontFamily: 'monospace' }}>
                          {secondaryFor(g)}
                        </Box>
                      </Box>
                    </li>
                  </span>
                </Tooltip>
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Go-Live"
                required
                helperText="Complete go-lives are disabled — reopen one to add more slips."
              />
            )}
          />

          {selected && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              {slipsQuery.isLoading ? 'Checking existing slips…' : numberingHelper}
            </div>
          )}

          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <label htmlFor="mac-input" className="eo-audit-label" style={{ marginBottom: 0 }}>
                MAC addresses
              </label>
              <span className="muted" style={{ fontSize: 12 }}>
                Scan tags straight into the box, or paste — lines, commas, or CSV
              </span>
            </div>
            <div style={{ display: 'flex' }}>
              <div className="eo-line-gutter" aria-hidden="true" data-testid="mac-line-gutter">
                <div className="eo-line-gutter-inner" ref={gutterRef}>
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i}>{i + 1}</div>
                  ))}
                </div>
              </div>
              <TextField
                multiline
                minRows={10}
                maxRows={16}
                fullWidth
                value={raw}
                onChange={e => setRaw(e.target.value)}
                placeholder="aa:bb:cc:dd:ee:ff"
                inputProps={{
                  id: 'mac-input',
                  'aria-label': 'MAC addresses',
                  onScroll: (e: React.UIEvent<HTMLTextAreaElement>) => {
                    if (gutterRef.current) gutterRef.current.scrollTop = e.currentTarget.scrollTop;
                  },
                  // lineHeight is pinned so the gutter rows (same 20px in
                  // .eo-line-gutter-inner) stay aligned with the textarea rows.
                  style: {
                    fontFamily: 'var(--edge-font-mono)',
                    fontSize: 13,
                    lineHeight: '20px',
                    letterSpacing: '0.01em',
                  },
                }}
              />
            </div>
          </div>

          {submitError && (
            <div className="eo-callout error" role="alert">
              <EoIcon name="error" />
              <span>{submitError}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="ea-btn contained" onClick={handleSubmit} disabled={!canSubmit}>
              <EoIcon name="playlist_add" />
              {createMutation.isPending
                ? 'Creating…'
                : `Create ${validCount} Tag Slip${validCount === 1 ? '' : 's'}`}
            </button>
            <button
              className="ea-btn ghost"
              onClick={() => setRaw('')}
              disabled={raw.length === 0 || createMutation.isPending}
            >
              Clear
            </button>
          </div>
        </div>

        {/* ---- Right column: live validation summary ---- */}
        <div className="ea-paper" style={{ padding: 18 }}>
          <div className="eo-audit-label" style={{ marginBottom: 12 }}>
            Validation
          </div>

          <div style={{ borderTop: '1px solid var(--edge-divider)' }}>
            <StatRow label="Lines parsed" value={parsed} icon="list_alt" testid="stat-parsed" />
            <StatRow
              label="Valid & unique"
              value={validCount}
              icon="check_circle"
              color="var(--edge-success)"
              testid="stat-valid"
            />
            <StatRow
              label="Duplicates removed"
              value={duplicates}
              icon="content_copy"
              color="var(--edge-warning)"
              testid="stat-duplicates"
            />
            <StatRow
              label="Invalid (skipped)"
              value={invalid.length}
              icon="error"
              color="var(--edge-error)"
              testid="stat-invalid"
            />
          </div>

          {invalid.length > 0 && (
            <div className="eo-callout error" style={{ marginTop: 14, flexDirection: 'column' }}>
              {invalid.slice(0, 3).map((entry, i) => (
                <div key={i}>
                  <span className="mono">Line {entry.line}: {entry.token}</span> is not a valid MAC address.
                </div>
              ))}
              {invalid.length > 3 && <div>…and {invalid.length - 3} more</div>}
            </div>
          )}

          {/* Numbered preview: physical tag slips are numbered, so showing each
              scanned MAC with the slip # it will get lets staff keep count
              mid-scan and match rows to slips before submitting. */}
          {validCount > 0 && (
            <div style={{ marginTop: 14 }} data-testid="slip-preview">
              <div className="eo-audit-label" style={{ marginBottom: 6 }}>
                Slip numbers
              </div>
              <div
                style={{
                  maxHeight: 240,
                  overflowY: 'auto',
                  border: '1px solid var(--edge-divider)',
                  borderRadius: 8,
                  padding: '6px 10px',
                }}
              >
                {valid.map((mac, i) => (
                  <div key={mac} style={{ display: 'flex', gap: 10, fontSize: 12.5, lineHeight: 1.8 }}>
                    <span className="muted" style={{ minWidth: 36, textAlign: 'right' }}>
                      #{(selected ? slipCount : 0) + 1 + i}
                    </span>
                    <span className="mono">{mac}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 14, marginBottom: 0 }}>
            Slips are numbered continuing this go-live's sequence. MACs are lowercased and checked against
            every other go-live when you submit.
          </p>
        </div>
      </Box>

      {snackbarElement}
    </EoPage>
  );
};

const StatRow: React.FC<{
  label: string;
  value: number;
  icon: string;
  color?: string;
  testid: string;
}> = ({ label, value, icon, color, testid }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '11px 2px',
      borderBottom: '1px solid var(--edge-divider)',
    }}
  >
    <span style={{ display: 'inline-flex', color: color ?? 'rgba(0, 0, 0, 0.5)' }}>
      <EoIcon name={icon} />
    </span>
    <span style={{ flex: 1, fontSize: 13, color: color ?? 'inherit' }}>{label}</span>
    <span data-testid={testid} className="mono" style={{ fontWeight: 600, color: color ?? 'inherit' }}>
      {value}
    </span>
  </div>
);
